/**
 * Admin panel: related-tunes score threshold + metadata dropdown lists.
 * Shown as a tab on /admin/templates.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from './ToastContainer';
import { DEFAULT_TUNE_METADATA_FIELD_OPTIONS } from '../constants/tuneMetadataFieldOptions';
import { invalidateTuneSiteOptionsCache } from '../utils/tuneSiteOptions';
import './AdminTuneSiteOptionsPanel.css';

const FIELD_LABELS = {
  region: 'Region',
  tune_type: 'Tune Type',
  key_signature: 'Key Signature',
  time_signature: 'Time Signature',
};

const FIELD_KEYS = Object.keys(FIELD_LABELS);

function emptyDraft() {
  return {
    relatedTunesMinScore: 15,
    tuneMetadataFieldOptions: JSON.parse(JSON.stringify(DEFAULT_TUNE_METADATA_FIELD_OPTIONS)),
  };
}

export default function AdminTuneSiteOptionsPanel({ authHeaders }) {
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState('tune_type');
  const [newValue, setNewValue] = useState('');
  const [draft, setDraft] = useState(emptyDraft);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/content/tune-site-options', { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to load tune options');
      const data = await res.json();
      setDraft({
        relatedTunesMinScore: data.relatedTunesMinScore ?? 15,
        tuneMetadataFieldOptions: data.tuneMetadataFieldOptions || emptyDraft().tuneMetadataFieldOptions,
      });
    } catch (err) {
      showError(err.message || 'Could not load tune options');
      setDraft(emptyDraft());
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const list = draft.tuneMetadataFieldOptions[activeField] || [];

  const addValue = () => {
    const v = newValue.trim();
    if (!v) return;
    const key = v.toLowerCase();
    if (list.some((x) => String(x).toLowerCase() === key)) {
      showError('That value is already in the list');
      return;
    }
    setDraft((prev) => ({
      ...prev,
      tuneMetadataFieldOptions: {
        ...prev.tuneMetadataFieldOptions,
        [activeField]: [...list, v],
      },
    }));
    setNewValue('');
  };

  const removeValue = (value) => {
    setDraft((prev) => ({
      ...prev,
      tuneMetadataFieldOptions: {
        ...prev.tuneMetadataFieldOptions,
        [activeField]: list.filter((x) => x !== value),
      },
    }));
  };

  const resetField = () => {
    if (!window.confirm(`Reset ${FIELD_LABELS[activeField]} to built-in defaults?`)) return;
    setDraft((prev) => ({
      ...prev,
      tuneMetadataFieldOptions: {
        ...prev.tuneMetadataFieldOptions,
        [activeField]: [...(DEFAULT_TUNE_METADATA_FIELD_OPTIONS[activeField] || [])],
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/content/templates/tune-site-options', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      const data = await res.json();
      setDraft({
        relatedTunesMinScore: data.relatedTunesMinScore ?? draft.relatedTunesMinScore,
        tuneMetadataFieldOptions: data.tuneMetadataFieldOptions || draft.tuneMetadataFieldOptions,
      });
      invalidateTuneSiteOptionsCache();
      showSuccess('Tune options saved');
    } catch (err) {
      showError(err.message || 'Could not save tune options');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="at-tune-options-loading">Loading tune options…</p>;
  }

  return (
    <div className="at-tune-options">
      <p className="at-tab-panel-intro">
        Configure how related tunes are ranked on tune pages, and manage dropdown presets used in the page editor and tune metadata admin.
      </p>

      <section className="at-tune-options-section">
        <h3 className="at-section-title">Related Tunes</h3>
        <label className="at-tune-options-label" htmlFor="related-tunes-min-score">
          Minimum match score (0–100)
        </label>
        <p className="at-tune-options-hint">
          Tag-based related tunes below this score are hidden. Explicit picks in Page Editor are always shown. Default: 15.
        </p>
        <input
          id="related-tunes-min-score"
          type="number"
          min={0}
          max={100}
          step={1}
          className="at-tune-options-score-input"
          value={draft.relatedTunesMinScore}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              relatedTunesMinScore: e.target.value,
            }))
          }
        />
      </section>

      <section className="at-tune-options-section">
        <h3 className="at-section-title">Metadata dropdowns</h3>
        <p className="at-tune-options-hint">
          These lists appear in tune forms (Page Editor, Tune Metadata). Removing a value does not change existing tunes that already use it.
        </p>

        <div className="at-tune-options-field-tabs" role="tablist" aria-label="Metadata field">
          {FIELD_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeField === key}
              className={`at-tune-options-field-tab ${activeField === key ? 'active' : ''}`}
              onClick={() => {
                setActiveField(key);
                setNewValue('');
              }}
            >
              {FIELD_LABELS[key]}
            </button>
          ))}
        </div>

        <div className="at-tune-options-add-row">
          <input
            type="text"
            className="at-tune-options-add-input"
            placeholder={`Add ${FIELD_LABELS[activeField]}…`}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addValue();
              }
            }}
          />
          <button type="button" className="btn btn-secondary" onClick={addValue}>
            + Add
          </button>
          <button type="button" className="btn btn-ghost" onClick={resetField}>
            Reset field
          </button>
        </div>

        <ul className="at-tune-options-value-list">
          {list.length === 0 ? (
            <li className="at-tune-options-empty">No values — add one above or reset to defaults.</li>
          ) : (
            list.map((value) => (
              <li key={value} className="at-tune-options-value-item">
                <span>{value}</span>
                <button
                  type="button"
                  className="at-tune-options-remove"
                  onClick={() => removeValue(value)}
                  aria-label={`Remove ${value}`}
                >
                  ×
                </button>
              </li>
            ))
          )}
        </ul>
      </section>

      <div className="at-tune-options-actions">
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save tune options'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={load} disabled={saving}>
          Reload
        </button>
      </div>
    </div>
  );
}
